import { getSessionToken, refresh } from '@descope/react-sdk';

export const DESCOPE_AUTH_ERROR_MESSAGE = 'Descope sign in authentication failed';
// The SDK can persist the session token a tick after a flow completes / redirect
// settles. Poll getSessionToken briefly before giving up.
const DESCOPE_SESSION_DISCOVERY_ATTEMPTS = 6;
const DESCOPE_SESSION_DISCOVERY_DELAY_MS = 150;

type DescopeExchangeErrorCode = 'TOKEN_EXPIRED' | 'TOKEN_INVALID' | 'TENANT_UNMAPPED' | 'SSO_EXCHANGE_FAILED';

type DescopeExchangeErrorPayload = {
    error: string;
    code: DescopeExchangeErrorCode | null;
    refreshable: boolean;
    status: number;
};

const wait = async (ms: number): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

export class DescopeExchangeError extends Error {
    readonly code: DescopeExchangeErrorCode | null;
    readonly refreshable: boolean;
    readonly status: number;

    constructor(payload: DescopeExchangeErrorPayload) {
        super(payload.error || DESCOPE_AUTH_ERROR_MESSAGE);
        this.name = 'DescopeExchangeError';
        this.code = payload.code;
        this.refreshable = payload.refreshable;
        this.status = payload.status;
    }
}

export const isDescopeExchangeError = (error: unknown): error is DescopeExchangeError =>
    error instanceof DescopeExchangeError;

/**
 * Resolve the Descope session JWT to hand to the platform exchange.
 *
 * In response-body mode the SDK stores the session token after a successful flow,
 * so the token from the flow's `onSuccess` event (passed as `preferredToken`) is
 * authoritative. We fall back to `getSessionToken()` for cases where the store
 * settles slightly after the event fires. `autoRefresh` keeps it current — we do
 * not call `refresh()` here.
 */
export const resolveDescopeSessionToken = async (preferredToken: string | null = null): Promise<string | null> => {
    if (preferredToken && preferredToken.trim().length > 0) {
        return preferredToken;
    }

    let token = getSessionToken();
    for (let attempt = 0; attempt < DESCOPE_SESSION_DISCOVERY_ATTEMPTS && !token; attempt += 1) {
        await wait(DESCOPE_SESSION_DISCOVERY_DELAY_MS);
        token = getSessionToken();
    }
    return token && token.trim().length > 0 ? token : null;
};

const safeRefreshSessionToken = async (): Promise<string | null> => {
    try {
        await refresh();
        const refreshed = getSessionToken();
        return refreshed && refreshed.trim().length > 0 ? refreshed : null;
    } catch (_error) {
        return null;
    }
};

export const exchangeDescopeTokenForAmpToken = async (descopeToken: string): Promise<string> => {
    const response = await fetch(`${process.env.PLATFORM_API_URL}/api/v1/auth/descope`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: descopeToken }),
    });

    let payload: Record<string, unknown> | null = null;
    try {
        payload = await response.json();
    } catch (_error) {
        payload = null;
    }

    const token = typeof payload?.token === 'string' ? payload.token : null;
    if (!response.ok || !token) {
        const error = typeof payload?.error === 'string' ? payload.error : DESCOPE_AUTH_ERROR_MESSAGE;
        const code = typeof payload?.code === 'string' ? (payload.code as DescopeExchangeErrorCode) : null;
        const refreshable = payload?.refreshable === true;
        throw new DescopeExchangeError({
            error: error || DESCOPE_AUTH_ERROR_MESSAGE,
            code,
            refreshable,
            status: response.status,
        });
    }

    return token;
};

/**
 * Exchange a Descope session token for a platform token. If the token was rejected
 * as expired and the API says it is refreshable, refresh once and retry — a safety
 * net for the rare case where the token lapses between resolution and exchange.
 */
export const exchangeDescopeTokenWithRefresh = async (descopeToken: string): Promise<string> => {
    try {
        return await exchangeDescopeTokenForAmpToken(descopeToken);
    } catch (error) {
        if (isDescopeExchangeError(error) && error.code === 'TOKEN_EXPIRED' && error.refreshable) {
            const refreshedToken = await safeRefreshSessionToken();
            if (refreshedToken) {
                return exchangeDescopeTokenForAmpToken(refreshedToken);
            }
        }
        throw error;
    }
};
